import React, { ReactElement, useState, useEffect } from "react";
import { Translate } from "react-redux-i18n";
import { useAppDispatch } from "redux/hooks";

import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { requestChangePassword, requestResetPassword } from "redux/auth/authSlice";


interface IPasswordChangeAndResetForm {
  isReset?: boolean
  isChangePass?: boolean
  view?: string
}
interface PasswordConfig {
  password: string;
  c_password: string
  o_password: string
}
interface User {
  username: string;
  email: string
}

import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import styles from "./styles.module.scss";

export default function PasswordChangeAndResetForm({ isReset, isChangePass, view }: IPasswordChangeAndResetForm): ReactElement {
  const dispatch = useAppDispatch();
  const [fieldsConfig, setFieldsConfig] = useState({
    isPasswordVisible: false,
    isChangedPasswordVisible: false,
    isOldPasswordVisible: false,
  });
  const [passwordConfig, setPasswordConfig] = useState<PasswordConfig>({
    password: "",
    c_password: "",
    o_password: ""
  });
  const [userConfig, setUserConfig] = useState<User>({
    username: "",
    email: "",
  });

  const [passwordError, setPasswordError] = useState(false);
  const [passwordLengthError, setPasswordLengthError] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const handleShowPassword = (type: string) => {
    switch (type) {
      case "changed-password":
        setFieldsConfig({
          ...fieldsConfig,
          isChangedPasswordVisible: !fieldsConfig.isChangedPasswordVisible,
        })
        break;
      case "old-password":
        setFieldsConfig({
          ...fieldsConfig,
          isOldPasswordVisible: !fieldsConfig.isOldPasswordVisible,
        })
        break;
      case "password":
        setFieldsConfig({
          ...fieldsConfig,
          isPasswordVisible: !fieldsConfig.isPasswordVisible,
        })
        break;

    }
  };

  const handleChange = (event: React.FormEvent) => {
    const { value, name } = event.target as HTMLInputElement;
    if (isReset) { return setPasswordConfig({ ...passwordConfig, [name]: value }) }
    else if (isChangePass) { return setPasswordConfig({ ...passwordConfig, [name]: value }) }
    else { return setUserConfig({ ...userConfig, [name]: value }) }

  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isChangePass) {
      dispatch(requestResetPassword({
        username: userConfig["username"],
        email: userConfig["email"]
      }))
    } else {
      dispatch(requestChangePassword({
        password: passwordConfig["password"],
        c_password: passwordConfig["c_password"],
      }));
      setPasswordConfig({
        password: "",
        c_password: "",
        o_password: ""
      })
    }
  };

  useEffect(() => {
    if (passwordConfig["password"].length < 5) {
      return setIsValid(false)
    }
    else if (passwordConfig["password"] !== passwordConfig["c_password"]) {
      return setIsValid(false)
    }
    else {
      return setIsValid(true)
    }
  }, [passwordConfig]);

  useEffect(() => {
    passwordConfig["password"] !== passwordConfig["c_password"]
      && passwordConfig["c_password"] !== "" ?
      setPasswordError(true)
      : passwordConfig["password"] === passwordConfig["c_password"] ?
        setPasswordError(false)
        : setPasswordError(false)

  }, [passwordConfig]);

  useEffect(() => {
    passwordConfig["password"].length < 5
      && passwordConfig["password"] !== "" ?
      setPasswordLengthError(true)
      : passwordConfig["password"].length >= 5 ?
        setPasswordLengthError(false)
        : setPasswordLengthError(false)

  }, [passwordConfig["password"]]);

  return (
    <form onSubmit={handleSubmit} className={`${styles.form} ${view === "enduser" && styles.smallChangePassword}`}>
      <Box className={styles.box}>
        {
          isReset || isChangePass ?
            <Box className={`${styles.textfieldContainer} ${view === "enduser" && styles.endUserTextfieldContainer}`}>
              <TextField
                name="password"
                value={passwordConfig["password"] || ""}
                id="Password"
                label={<Translate value="userInformation.password" />}
                size="small"
                type={fieldsConfig["isPasswordVisible"] ? 'text' : 'password'}
                error={passwordLengthError}
                helperText={passwordLengthError && "Use at least 5 characters"}
                fullWidth
                required
                onChange={handleChange}
                className={[styles.textfield, "form-label", view === "enduser" && styles.fullWidthInput].join(" ")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={() => handleShowPassword("password")}
                        size='small'
                      >
                        {!passwordConfig["password"] ?
                          <FontAwesomeIcon type="fas" name="eye" />
                          : <FontAwesomeIcon type="fas" name="eye-slash" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                name="c_password"
                value={passwordConfig["c_password"] || ""}
                id="Confirm Password"
                label={<Translate value="userInformation.c_password" />}
                size="small"
                type={fieldsConfig["isChangedPasswordVisible"] ? 'text' : 'password'}
                error={passwordError}
                helperText={passwordError && <Translate value="userInformation.mismatch" />}
                fullWidth
                required
                onChange={handleChange}
                className={[styles.textfield, "form-label", view === "enduser" && styles.fullWidthInput].join(" ")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={() => handleShowPassword("changed-password")}
                        size='small'
                      >
                        {!fieldsConfig["isChangedPasswordVisible"] ?
                          <FontAwesomeIcon type="fas" name="eye" />
                          : <FontAwesomeIcon type="fas" name="eye-slash" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            :
            <Box className={styles.textfieldContainer}>
              <TextField
                name="username"
                value={userConfig["username"] || ""}
                label="username"
                id="username"
                size="small"
                fullWidth
                required
                onChange={handleChange}
                className={[styles.textfield, "form-label"].join(" ")}
              />
              <TextField
                name="email"
                value={userConfig["email"] || ""}
                label="email"
                id="email"
                size="small"
                fullWidth
                required
                onChange={handleChange}
                className={[styles.textfield, "form-label"].join(" ")}
              />
            </Box>
        }
        {isChangePass ?
          <Button
            variant="contained"
            type="submit"
            disabled={isValid ? false : true}
            color="success"
            className={styles.submitBtn}
          >
            <Translate value="buttons.save" />
          </Button>
          :
          <Button
            type="submit"
            size="small"
            color="success"
            variant="contained"
            className={styles.submitBtn}
          >
            <Translate value="buttons.proceed" />
          </Button>}
      </Box>
    </form >
  );
}